class BrandPage extends HTMLElement {
  #searchInput = null;
  #clearButton = null;
  #abortController = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const { signal } = this.#abortController;

    this.#searchInput = this.querySelector('[data-brand-search]');
    this.#clearButton = this.querySelector('[data-brand-search-clear]');

    this.#syncSections();
    this.#bindNav(signal);
    this.#bindSearch(signal);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#searchInput = null;
    this.#clearButton = null;
  }

  #getAnchorMap() {
    const anchors = new Map();

    this.querySelectorAll('[data-brand-anchor]').forEach((anchor) => {
      const sectionId = anchor.dataset.brandAnchor;

      if (sectionId) {
        anchors.set(sectionId, anchor);
      }
    });

    return anchors;
  }

  #countVendors(section, normalizedQuery, isFiltering) {
    const vendors = section.querySelectorAll('.brand-page__vendor');
    let visible = 0;

    vendors.forEach((vendor) => {
      const name = vendor.dataset.vendorName ?? '';
      const matches = !isFiltering || name.includes(normalizedQuery);

      vendor.classList.toggle('hidden', !matches);

      if (matches) {
        visible += 1;
      }
    });

    return { catalog: vendors.length, visible };
  }

  #syncSections(query = '') {
    const normalizedQuery = query.trim().toLowerCase();
    const isFiltering = normalizedQuery.length > 0;
    const anchorMap = this.#getAnchorMap();
    let totalVisible = 0;

    this.querySelectorAll('[data-brand-section]').forEach((section) => {
      const { catalog, visible } = this.#countVendors(section, normalizedQuery, isFiltering);

      totalVisible += visible;

      const hasCatalogVendors = catalog > 0;
      const hasVisibleVendors = visible > 0;

      section.classList.toggle('hidden', isFiltering ? !hasVisibleVendors : !hasCatalogVendors);

      const anchor = anchorMap.get(section.id);

      if (anchor) {
        const disableLetter = isFiltering && hasCatalogVendors && !hasVisibleVendors;

        anchor.classList.toggle('hidden', !hasCatalogVendors);
        this.#setAnchorDisabled(anchor, disableLetter);
      }
    });

    const noResults = this.querySelector('[data-brand-no-results]');

    if (noResults) {
      noResults.classList.toggle('hidden', !isFiltering || totalVisible > 0);
    }

    this.#updateVendorCount(totalVisible, isFiltering);
  }

  #updateVendorCount(totalVisible, isFiltering) {
    const countEl = this.querySelector('[data-brand-count]');

    if (!countEl) {
      return;
    }

    if (!isFiltering) {
      countEl.dataset.brandCountTotal = String(totalVisible);
    }

    const total = Number.parseInt(countEl.dataset.brandCountTotal ?? '0', 10);
    const displayCount = isFiltering ? totalVisible : total;

    countEl.textContent = `( ${displayCount} )`;
  }

  #setAnchorDisabled(anchor, disabled) {
    anchor.classList.toggle('is-disabled', disabled);
    anchor.toggleAttribute('aria-disabled', disabled);

    const href = anchor.dataset.href;

    if (disabled) {
      // Drop href so assistive tech does not treat the letter as an active link.
      anchor.removeAttribute('href');
      anchor.setAttribute('tabindex', '-1');
      return;
    }

    if (href) {
      anchor.setAttribute('href', href);
    }

    anchor.removeAttribute('tabindex');
  }

  #applySearch(query) {
    const searchQuery = query ?? this.#searchInput?.value ?? '';

    this.#syncSections(searchQuery);
    this.#updateClearButton();
  }

  #updateClearButton() {
    if (!this.#clearButton || !this.#searchInput) {
      return;
    }

    const hasValue = this.#searchInput.value.length > 0;

    this.#clearButton.classList.toggle('hidden', !hasValue);
    this.#clearButton.toggleAttribute('hidden', !hasValue);
  }

  #clearSearch() {
    if (!this.#searchInput) {
      return;
    }

    this.#searchInput.value = '';
    this.#searchInput.focus();
    this.#applySearch('');
  }

  #bindSearch(signal) {
    if (!this.#searchInput) {
      return;
    }

    this.#searchInput.addEventListener('input', () => this.#applySearch(), { signal });

    this.#clearButton?.addEventListener('click', () => this.#clearSearch(), { signal });

    this.#updateClearButton();
  }

  #bindNav(signal) {
    this.querySelectorAll('[data-brand-anchor]').forEach((anchor) => {
      anchor.addEventListener(
        'click',
        (event) => {
          const targetId = anchor.dataset.brandAnchor;
          const target = targetId
            ? this.querySelector(`#${CSS.escape(targetId)}`)
            : null;

          if (!target || target.classList.contains('hidden') || !anchor.hasAttribute('href')) {
            return;
          }

          event.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        },
        { signal }
      );
    });
  }
}

if (!window.customElements.get('brand-page')) {
  window.customElements.define('brand-page', BrandPage);
}
